import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Search, MapPin, Plus, X, MessageSquare } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

function TagInput({ value, onChange, placeholder, suggestions, testid }) {
  const { t } = useLang();
  const [draft, setDraft] = useState("");
  const add = (t) => {
    const v = (t || draft).trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-wine-100 px-2.5 py-0.5 text-xs font-semibold text-wine-700" data-testid={`${testid}-tag-${t}`}>
            {t}<button onClick={() => onChange(value.filter((x) => x !== t))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid={`${testid}-input`} />
        <button onClick={() => add()} className="rounded-full bg-wine-600 px-3 py-2 text-xs font-bold text-white hover:bg-wine-700"><Plus className="h-3 w-3" /></button>
      </div>
      {suggestions?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          <span className="text-[10px] uppercase text-neutral-400">{t("portal_matchmaking_01")}</span>
          {suggestions.filter((s) => !value.includes(s.tag)).slice(0, 8).map((s) => (
            <button key={s.tag} onClick={() => add(s.tag)} className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600 hover:border-wine-300 hover:text-wine-700" data-testid={`${testid}-suggest-${s.tag}`}>+ {s.tag}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Matchmaking() {
  const { t } = useLang();
  const [me, setMe] = useState({ offering_tags: [], seeking_tags: [], company_name: "" });
  const [matches, setMatches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/matchmaking/me"), api.get("/matchmaking/tags"), api.get("/matchmaking/matches")])
      .then(([m, t, mm]) => { if (mounted) { setMe(m.data); setSuggestions(t.data); setMatches(mm.data); } })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/matchmaking/me", { offering_tags: me.offering_tags, seeking_tags: me.seeking_tags });
      toast.success("Profil mis à jour");
      const m = await api.get("/matchmaking/matches"); setMatches(m.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendIntro = async (matchedCompany) => {
    // Find a contact from the matched company to message
    try {
      const r = await api.get("/messages/users", { params: { q: matchedCompany.name } });
      const user = r.data[0];
      if (!user) { toast.error("Aucun utilisateur trouvé pour ce membre"); return; }
      await api.post("/messages/new", {
        to_user_id: user.id,
        subject: `Matchmaking — connexion via La WineTech`,
        body: `Bonjour ${user.name},\n\nVotre fiche est apparue dans mes correspondances La WineTech.\nNos centres d'intérêt se croisent (${[...matchedCompany.they_offer_what_i_seek, ...matchedCompany.i_offer_what_they_seek].join(", ")}).\n\nSeriez-vous disponible pour un premier échange ?\n\nÀ très vite,\n${me.company_name}`,
      });
      toast.success(`Message envoyé à ${user.name}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <PortalLayout sidebar="member">
      <div>
        <h1 className="text-3xl font-bold text-navy md:text-4xl"><Sparkles className="mr-2 inline h-7 w-7 text-amber-500" />Matchmaking</h1>
        <p className="mt-1 text-neutral-600">Indiquez ce que vous <strong>proposez</strong>{t("portal_matchmaking_02")}<strong>cherchez</strong>{t("portal_matchmaking_03")}</p>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/30 p-5">
          <h2 className="text-sm font-bold text-emerald-700">🌟 Je propose</h2>
          <p className="mt-1 text-xs text-neutral-600">{t("portal_matchmaking_04")}</p>
          <div className="mt-3"><TagInput value={me.offering_tags || []} onChange={(v) => setMe({ ...me, offering_tags: v })} placeholder="Ex: capteurs IoT, distribution UK…" suggestions={suggestions} testid="offering" /></div>
        </div>
        <div className="rounded-2xl border border-blue-200 bg-blue-50/30 p-5">
          <h2 className="text-sm font-bold text-blue-700">🔎 Je cherche</h2>
          <p className="mt-1 text-xs text-neutral-600">{t("portal_matchmaking_05")}</p>
          <div className="mt-3"><TagInput value={me.seeking_tags || []} onChange={(v) => setMe({ ...me, seeking_tags: v })} placeholder="Ex: investisseurs, importateur US…" suggestions={suggestions} testid="seeking" /></div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="mt-4 inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="match-save"><Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer et trouver mes matchs"}</button>

      <h2 className="mt-10 text-xl font-bold text-navy">🤝 Vos correspondances ({matches.length})</h2>
      {matches.length === 0 && (
        <div className="mt-4 rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-12 text-center text-sm text-neutral-500" data-testid="matches-empty">
          {(me.offering_tags || []).length + (me.seeking_tags || []).length === 0
            ? "Renseignez d&apos;abord ce que vous proposez et cherchez."
            : "Aucune correspondance pour le moment — revenez plus tard !"}
        </div>
      )}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {matches.map((m) => (
          <div key={m.id} className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid={`match-${m.id}`}>
            <div className="flex items-start gap-3">
              {m.logo && <img src={resolveUrl(m.logo)} alt={m.name} className="h-12 w-12 rounded-xl object-contain bg-white p-1" />}
              <div className="flex-1 min-w-0">
                <Link to={`/members/${m.slug}`} className="font-bold text-neutral-900 hover:underline">{m.name}</Link>
                <div className="text-xs text-neutral-500"><MapPin className="inline h-3 w-3" /> {m.city}, {m.country}</div>
              </div>
              <div className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Score {m.score}</div>
            </div>
            <p className="mt-2 line-clamp-2 text-xs text-neutral-600">{m.description}</p>
            {m.they_offer_what_i_seek?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-bold uppercase text-emerald-700">Ils proposent ce que vous cherchez</div>
                <div className="mt-1 flex flex-wrap gap-1">{m.they_offer_what_i_seek.map((t) => <span key={t} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">{t}</span>)}</div>
              </div>
            )}
            {m.i_offer_what_they_seek?.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-bold uppercase text-blue-700">Vous proposez ce qu&apos;ils cherchent</div>
                <div className="mt-1 flex flex-wrap gap-1">{m.i_offer_what_they_seek.map((t) => <span key={t} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800">{t}</span>)}</div>
              </div>
            )}
            <button onClick={() => sendIntro(m)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-wine-50 px-3 py-1.5 text-xs font-bold text-wine-700 hover:bg-wine-100" data-testid={`match-contact-${m.id}`}><MessageSquare className="h-3 w-3" /> Prendre contact</button>
          </div>
        ))}
      </div>
    </PortalLayout>
  );
}
